import React, {useState} from "react";
import {INormalizedProduct} from "../../models/NormalizedProduct";
import {Checkbox, CommandBar, ICommandBarItemProps, PrimaryButton, Separator, Stack, Text, useTheme} from "@fluentui/react";
import {toast} from "react-toastify";
import useCan, {EDIT_ITEM, STOREFRONT_ADMIN} from "../../permissions/ability";
import {
    useDeleteProductLinkedItemMutation,
    useGetLinkedItemTypesQuery,
    useGetMultipleItemDetailsQuery,
    useGetProductLinkedItemsQuery,
    useSaveProductLinkedItemsMutation
} from "../../store/Api";
import {IProductLinkedItem, IProductLinkedItemEntry} from "../../models/LinkedItems";
import {CuratedLinkedItemsTable} from "./CuratedLinkedItemsTable";
import {SuggestedLinksTable} from "./SuggestedLinksTable";
import AddLinkedItemPanel from "./AddLinkedItemPanel";
import {passesStockFilter} from "./linkedItemHelpers";
import BulkStorefrontPanel from "../Storefront/BulkStorefrontPanel";
import DetailsModal from "./DetailsModal";

interface ILinkedItemsDetailsProps {
    product: INormalizedProduct;
}

const LEGACY_TYPES = ['services', 'accessories', 'replacements', 'alternatives'];

// Linked items live in their own index and save immediately, independent of the
// product's edit mode — so all linked-item controls stay available to permitted users.
const LinkedItemsDetails: React.FC<ILinkedItemsDetailsProps> = (props) => {
    const {product} = props;
    const theme = useTheme();
    const canEdit = useCan(EDIT_ITEM);
    const canStorefrontAdmin = useCan(STOREFRONT_ADMIN);

    const [filterStock, setFilterStock] = useState<boolean>(false);
    const [panelOpen, setPanelOpen] = useState<boolean>(false);
    const [selectedSuggested, setSelectedSuggested] = useState<Set<string>>(new Set());
    const [selectedForStorefront, setSelectedForStorefront] = useState<Set<string>>(new Set());
    const [storefrontPanelOpen, setStorefrontPanelOpen] = useState<boolean>(false);
    // A read-only DetailsModal nested inside this one, showing whichever linked/suggested row was clicked.
    const [viewProduct, setViewProduct] = useState<INormalizedProduct | undefined>(undefined);

    const toggleStorefrontSelect = (productId: string, checked: boolean) => {
        setSelectedForStorefront(prev => {
            const next = new Set(prev);
            if (checked) next.add(productId);
            else next.delete(productId);
            return next;
        });
    };

    const linkedItemsQuery = useGetProductLinkedItemsQuery({catalog: product.catalog, productId: product.id});
    const linkedItems: IProductLinkedItem[] = linkedItemsQuery.data ?? [];

    const typesQuery = useGetLinkedItemTypesQuery({catalog: product.catalog});
    const typeOptions: string[] = typesQuery.data ?? [];

    const [saveLinks] = useSaveProductLinkedItemsMutation();
    const [deleteLink] = useDeleteProductLinkedItemMutation();

    // Resolve curated linked-item product details.
    const curatedIds = linkedItems.map(l => l.linkedProductId);
    const curatedProductsQuery = useGetMultipleItemDetailsQuery(
        {catalog: product.catalog, ids: curatedIds},
        {skip: curatedIds.length === 0}
    );
    const curatedProductMap: Record<string, INormalizedProduct> = {};
    (curatedProductsQuery.data ?? []).forEach(p => {
        curatedProductMap[p.id] = p;
    });
    const entryLookup: Record<string, IProductLinkedItem> = {};
    linkedItems.forEach(l => {
        entryLookup[l.linkedProductId] = l;
    });
    const curatedIdSet = new Set(curatedIds);

    // Group curated links by type.
    const curatedGroups: Record<string, IProductLinkedItem[]> = {};
    linkedItems.forEach(l => {
        (curatedGroups[l.type] = curatedGroups[l.type] ?? []).push(l);
    });
    const curatedGroupTypes = Object.keys(curatedGroups).sort();

    const productsForCuratedType = (items: IProductLinkedItem[]): INormalizedProduct[] =>
        items
            .map(l => curatedProductMap[l.linkedProductId])
            .filter((p): p is INormalizedProduct => !!p)
            .filter(p => !filterStock || passesStockFilter(p));

    // Suggested (legacy vendor-feed) links.
    const suggestedLinks = product.links ?? [];
    const suggestedIds = suggestedLinks.map(l => l.value);
    const suggestedProductsQuery = useGetMultipleItemDetailsQuery(
        {catalog: product.catalog, ids: suggestedIds},
        {skip: suggestedIds.length === 0}
    );
    const suggestedProductMap: Record<string, INormalizedProduct> = {};
    (suggestedProductsQuery.data ?? []).forEach(p => {
        suggestedProductMap[p.id] = p;
    });

    const selectableSelectedCount = suggestedLinks.filter(l => selectedSuggested.has(l.value) && !curatedIdSet.has(l.value)).length;

    const handleSaveLink = async (entry: IProductLinkedItemEntry) => {
        const toastId = toast.loading('Saving linked item…');
        try {
            await saveLinks({catalog: product.catalog, productId: product.id, links: [entry]}).unwrap();
            toast.update(toastId, {type: 'success', render: 'Linked item saved', isLoading: false, autoClose: 3000});
        } catch {
            toast.update(toastId, {type: 'error', render: 'Failed to save linked item', isLoading: false, autoClose: 4000});
        }
    };

    const handleDeleteLink = async (linkedProductId: string) => {
        const toastId = toast.loading('Removing linked item…');
        try {
            await deleteLink({catalog: product.catalog, productId: product.id, linkedProductId}).unwrap();
            toast.update(toastId, {type: 'success', render: 'Linked item removed', isLoading: false, autoClose: 3000});
        } catch {
            toast.update(toastId, {type: 'error', render: 'Failed to remove linked item', isLoading: false, autoClose: 4000});
        }
    };

    const toggleSuggestedSelect = (productId: string, checked: boolean) => {
        setSelectedSuggested(prev => {
            const next = new Set(prev);
            if (checked) next.add(productId);
            else next.delete(productId);
            return next;
        });
    };

    const handleCopySuggested = async () => {
        const toCopy = suggestedLinks.filter(l => selectedSuggested.has(l.value) && !curatedIdSet.has(l.value));
        if (toCopy.length === 0) return;
        const links: IProductLinkedItemEntry[] = toCopy.map(l => ({
            linkedProductId: l.value,
            type: l.type,
            recommended: l.recommended
        }));
        const count = links.length;
        const noun = `suggested link${count === 1 ? '' : 's'}`;
        const toastId = toast.loading(`Copying ${count} ${noun}…`);
        try {
            await saveLinks({catalog: product.catalog, productId: product.id, links}).unwrap();
            toast.update(toastId, {type: 'success', render: `Copied ${count} ${noun}`, isLoading: false, autoClose: 3000});
            setSelectedSuggested(new Set());
        } catch {
            toast.update(toastId, {type: 'error', render: 'Failed to copy suggested links', isLoading: false, autoClose: 4000});
        }
    };

    const commandBarItems: ICommandBarItemProps[] = [
        ...(canEdit ? [{
            key: 'addLinkedItem',
            text: 'Add Linked Item',
            iconProps: {iconName: 'Add'},
            onClick: () => setPanelOpen(true),
        }] : []),
        ...(canStorefrontAdmin && selectedForStorefront.size > 0 ? [{
            key: 'listOnStorefront',
            text: `List on Storefront (${selectedForStorefront.size})`,
            iconProps: {iconName: 'Shop'},
            onClick: () => setStorefrontPanelOpen(true),
        }] : []),
    ];
    const commandBarFarItems: ICommandBarItemProps[] = [{
        key: 'inStockOnly',
        onRender: () => (
            <div style={{display: 'flex', alignItems: 'center', paddingLeft: '0.5em'}}>
                <Checkbox
                    label="In Stock Only"
                    checked={filterStock}
                    onChange={(_e, d) => setFilterStock(d ?? false)}
                />
            </div>
        ),
    }];

    return (
        <>
            <Stack tokens={{childrenGap: '1em'}}>
                <h3 style={{margin: 0}}>Linked Items</h3>
                <CommandBar
                    items={commandBarItems}
                    farItems={commandBarFarItems}
                    styles={{root: {padding: 0}}}
                />
                {curatedGroupTypes.length === 0
                    ? <Text styles={{root: {color: theme.palette.neutralSecondary}}}>No linked items yet.</Text>
                    : curatedGroupTypes.map(type => (
                        <CuratedLinkedItemsTable
                            key={type}
                            type={type}
                            products={productsForCuratedType(curatedGroups[type])}
                            entryLookup={entryLookup}
                            typeOptions={typeOptions}
                            loading={curatedProductsQuery.isLoading}
                            canEdit={canEdit}
                            onSaveLink={handleSaveLink}
                            onDeleteLink={handleDeleteLink}
                            selectionEnabled={canStorefrontAdmin}
                            selectedProductIds={selectedForStorefront}
                            onToggleProductSelect={toggleStorefrontSelect}
                            onViewDetails={setViewProduct}
                        />
                    ))}
            </Stack>

            {suggestedLinks.length > 0 &&
                <Stack tokens={{childrenGap: '0.5em'}} styles={{root: {marginTop: '1em'}}}>
                    <Separator/>
                    <h3 style={{margin: 0}}>Suggested Links</h3>
                    <Text variant="small" styles={{root: {color: theme.palette.neutralSecondary}}}>
                        Provided by product data feeds. Copy links to curate what shows on your storefronts.
                    </Text>
                    {canEdit &&
                        <div>
                            <PrimaryButton
                                iconProps={{iconName: 'Copy'}}
                                text={`Copy to Linked Items${selectableSelectedCount > 0 ? ` (${selectableSelectedCount})` : ''}`}
                                disabled={selectableSelectedCount === 0}
                                onClick={handleCopySuggested}
                            />
                        </div>}
                    {LEGACY_TYPES.map(type => {
                        const items = suggestedLinks.filter(l => l.type === type);
                        if (items.length === 0) return null;
                        const products = items
                            .map(l => suggestedProductMap[l.value])
                            .filter((p): p is INormalizedProduct => !!p);
                        return (
                            <SuggestedLinksTable
                                key={type}
                                type={type}
                                products={products}
                                loading={suggestedProductsQuery.isLoading}
                                canEdit={canEdit}
                                curatedIds={curatedIdSet}
                                selectedIds={selectedSuggested}
                                onToggleSelect={toggleSuggestedSelect}
                                onViewDetails={setViewProduct}
                            />
                        );
                    })}
                </Stack>}

            {panelOpen &&
                <AddLinkedItemPanel
                    isOpen={panelOpen}
                    onDismiss={() => setPanelOpen(false)}
                    catalog={product.catalog}
                    productId={product.id}
                    existingLinkedIds={curatedIdSet}
                />}

            {storefrontPanelOpen &&
                <BulkStorefrontPanel
                    isOpen={storefrontPanelOpen}
                    onDismiss={() => setStorefrontPanelOpen(false)}
                    catalog={product.catalog}
                    productIds={Array.from(selectedForStorefront)}
                    onApplied={() => setSelectedForStorefront(new Set())}
                />}

            {viewProduct &&
                <DetailsModal
                    product={viewProduct.id}
                    catalog={viewProduct.catalog}
                    open={true}
                    onClose={() => setViewProduct(undefined)}
                    preventEdit={!canEdit}
                />}
        </>
    );
};

export default LinkedItemsDetails;
